import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Topbar } from '@/components/Topbar';
import { useAsync } from '@/hooks/useAsync';
import { haptic } from '@/lib/telegram';
import type { CampaignTeamMember } from '@/types/domain';

export function CampaignTeamPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data: campaign, loading: campaignLoading, error: campaignError, reload: reloadCampaign } = useAsync(
    () => api.getCampaign(cid),
    [cid],
  );
  const { data: team, loading: teamLoading, error: teamError, reload: reloadTeam } = useAsync(
    () => api.listCampaignTeam(cid),
    [cid],
  );
  const [teamInput, setTeamInput] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loading = campaignLoading || teamLoading;
  const isOwner = campaign?.accessRole === 'owner';
  const ownerLabel = campaign?.owner?.username
    ? `@${campaign.owner.username}`
    : campaign?.owner?.firstName || campaign?.owner?.tgId || 'не назначен';
  const members = [...(team ?? [])].sort((left, right) => {
    if (left.role === right.role) return left.userId - right.userId;
    return left.role === 'owner' ? -1 : 1;
  });

  const refresh = () => {
    reloadCampaign();
    reloadTeam();
  };

  const addMember = async () => {
    const value = teamInput.trim();
    if (!campaign || !isOwner || !value) return;
    setSubmitBusy(true);
    setActionError(null);
    try {
      await api.addCampaignTeamMember(campaign.id, {
        tgId: /^\d+$/.test(value) ? value : undefined,
        username: /^\d+$/.test(value) ? undefined : value,
      });
      setTeamInput('');
      haptic('success');
      refresh();
    } catch (error) {
      haptic('warning');
      setActionError(error instanceof Error ? error.message : 'Не удалось добавить участника');
    } finally {
      setSubmitBusy(false);
    }
  };

  const removeMember = async (member: CampaignTeamMember) => {
    if (!campaign || !isOwner || member.role === 'owner') return;
    const label = member.username ? `@${member.username}` : member.firstName || member.tgId;
    if (!confirm(`Удалить ${label} из команды?`)) return;
    setActionError(null);
    try {
      await api.removeCampaignTeamMember(campaign.id, member.userId);
      haptic('success');
      refresh();
    } catch (error) {
      haptic('warning');
      setActionError(error instanceof Error ? error.message : 'Не удалось удалить участника');
    }
  };

  return (
    <div className="space-y-4 pb-8">
      <Topbar
        title="Участники"
        subtitle={
          campaign
            ? `${campaign.isOwned ? 'моя кампания' : 'командная'} · owner ${ownerLabel}`
            : 'доступы кампании'
        }
        right={
          campaign ? (
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-300">
              {campaign.accessRole === 'owner' ? 'owner' : 'member'}
            </span>
          ) : undefined
        }
      />

      {loading ? (
        <div className="space-y-3">
          <div className="skeleton h-24" />
          <div className="skeleton h-16" />
          <div className="skeleton h-16" />
        </div>
      ) : null}

      {!loading && !campaign ? (
        <EmptyState
          icon="search"
          title="Кампания недоступна"
          body={campaignError || 'Не удалось загрузить кампанию или у вас больше нет доступа.'}
        />
      ) : null}

      {campaign ? (
        <>
          <section className="card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-ink-50">Команда кампании</div>
                <div className="mt-1 text-xs leading-5 text-ink-500">
                  Владелец: {ownerLabel} · участников {campaign.teamSize ?? members.length}
                </div>
              </div>
              <span className="pill pill-good shrink-0">{members.length}</span>
            </div>
            <div className="mt-4 rounded-2xl border border-ink-700/60 bg-ink-900/70 px-4 py-3 text-xs leading-5 text-ink-300">
              {isOwner
                ? 'Добавляй пользователей по tg_id или по username. Username сработает, если пользователь уже есть в системе.'
                : 'Ты видишь состав команды этой кампании, но управлять участниками может только owner.'}
            </div>
          </section>

          {isOwner ? (
            <section className="card p-4">
              <div className="text-sm font-semibold text-ink-50">Добавить участника</div>
              <div className="mt-1 text-xs text-ink-500">Укажи `@username` или числовой `tg_id`.</div>
              <div className="mt-4 flex gap-2">
                <input
                  value={teamInput}
                  onChange={(event) => setTeamInput(event.target.value)}
                  placeholder="@username или tg_id"
                  className="min-w-0 flex-1 rounded-[18px] border border-ink-700/60 bg-ink-900/70 px-4 py-3 text-sm text-ink-50 outline-none placeholder:text-ink-500"
                />
                <button
                  type="button"
                  onClick={addMember}
                  disabled={submitBusy || !teamInput.trim()}
                  className="btn-primary shrink-0"
                >
                  <Icon name="plus" size={16} />
                  {submitBusy ? 'Добавляю…' : 'Добавить'}
                </button>
              </div>
              {actionError ? (
                <div className="mt-3 text-xs text-rose-300">{actionError}</div>
              ) : null}
            </section>
          ) : null}

          {teamError && !actionError ? (
            <section className="card border border-rose-500/20 bg-rose-500/5 p-4 text-sm text-rose-200">
              {teamError}
            </section>
          ) : null}

          <section className="space-y-2">
            {members.map((member) => {
              const title = member.username ? `@${member.username}` : member.firstName || member.tgId;
              const subtitle = [member.firstName, member.lastName].filter(Boolean).join(' ').trim() || member.tgId;
              const invitedBy = member.invitedBy?.username
                ? `@${member.invitedBy.username}`
                : member.invitedBy?.firstName || member.invitedBy?.tgId || null;

              return (
                <article key={member.userId} className="card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold text-ink-50">{title}</div>
                        <span
                          className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold ${
                            member.role === 'owner'
                              ? 'bg-amber-500/15 text-amber-200'
                              : 'bg-white/8 text-[#c9d3ff]'
                          }`}
                        >
                          {member.role}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-ink-500">{subtitle}</div>
                      <div className="mt-2 text-[11px] uppercase tracking-[0.14em] text-ink-600">
                        tg_id {member.tgId}
                        {invitedBy ? ` · invited by ${invitedBy}` : ''}
                      </div>
                    </div>
                    {isOwner && member.role !== 'owner' ? (
                      <button
                        type="button"
                        onClick={() => removeMember(member)}
                        className="rounded-full bg-rose-500/15 px-3 py-1 text-[11px] font-semibold text-rose-200"
                      >
                        Удалить
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}

            {!members.length ? (
              <div className="card p-4 text-sm text-ink-500">Кроме владельца пока никого нет.</div>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
